import _slicedToArray from "/codebuild/output/src3305341033/src/hvorigins/node_modules/@babel/runtime/helpers/esm/slicedToArray";
import _asyncToGenerator from "/codebuild/output/src3305341033/src/hvorigins/node_modules/@babel/runtime/helpers/esm/asyncToGenerator";
import _regeneratorRuntime from "/codebuild/output/src3305341033/src/hvorigins/node_modules/@babel/runtime/regenerator";
import { struct, u32, u8 } from '@solana/buffer-layout';
import { bool, publicKey, u64 } from '@solana/buffer-layout-utils';
import { PublicKey } from '@solana/web3.js';
import { ASSOCIATED_TOKEN_PROGRAM_ID, TOKEN_PROGRAM_ID } from '../constants.js';
import { TokenAccountNotFoundError, TokenInvalidAccountOwnerError, TokenInvalidAccountSizeError, TokenInvalidMintError, TokenOwnerOffCurveError } from '../errors.js';
import { ACCOUNT_TYPE_SIZE, AccountType } from '../extensions/accountType.js';
import { getMintLen } from '../extensions/extensionType.js';
import { ACCOUNT_SIZE } from './account.js';
import { MULTISIG_SIZE } from './multisig.js';
/** Buffer layout for de/serializing a mint */

export var MintLayout = struct([u32('mintAuthorityOption'), publicKey('mintAuthority'), u64('supply'), u8('decimals'), bool('isInitialized'), u32('freezeAuthorityOption'), publicKey('freezeAuthority')]);
/** Byte length of a mint */

export var MINT_SIZE = MintLayout.span;
/**
 * Retrieve information about a mint
 *
 * @param connection Connection to use
 * @param address    Mint account
 * @param commitment Desired level of commitment for querying the state
 * @param programId  SPL Token program account
 *
 * @return Mint information
 */

export function getMint(_x, _x2, _x3) {
  return _getMint.apply(this, arguments);
}
/**
 * Unpack a mint
 *
 * @param address   Mint account
 * @param info      Mint account data
 * @param programId SPL Token program account
 *
 * @return Unpacked mint
 */

function _getMint() {
  _getMint = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(connection, address, commitment) {
    var programId,
        info,
        _args = arguments;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            programId = _args.length > 3 && _args[3] !== undefined ? _args[3] : TOKEN_PROGRAM_ID;
            _context.next = 3;
            return connection.getAccountInfo(address, commitment);

          case 3:
            info = _context.sent;
            return _context.abrupt("return", unpackMint(address, info, programId));

          case 5:
          case "end":
            return _context.stop();
        }
      }
    }, _callee);
  }));
  return _getMint.apply(this, arguments);
}

export function unpackMint(address, info) {
  var programId = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : TOKEN_PROGRAM_ID;
  if (!info) throw new TokenAccountNotFoundError();
  if (!info.owner.equals(programId)) throw new TokenInvalidAccountOwnerError();
  if (info.data.length < MINT_SIZE) throw new TokenInvalidAccountSizeError();
  var rawMint = MintLayout.decode(info.data.slice(0, MINT_SIZE));
  var tlvData = Buffer.alloc(0);

  if (info.data.length > MINT_SIZE) {
    if (info.data.length <= ACCOUNT_SIZE) throw new TokenInvalidAccountSizeError();
    if (info.data.length === MULTISIG_SIZE) throw new TokenInvalidAccountSizeError();
    if (info.data[ACCOUNT_SIZE] != AccountType.Mint) throw new TokenInvalidMintError();
    tlvData = info.data.slice(ACCOUNT_SIZE + ACCOUNT_TYPE_SIZE);
  }

  return {
    address: address,
    mintAuthority: rawMint.mintAuthorityOption ? rawMint.mintAuthority : null,
    supply: rawMint.supply,
    decimals: rawMint.decimals,
    isInitialized: rawMint.isInitialized,
    freezeAuthority: rawMint.freezeAuthorityOption ? rawMint.freezeAuthority : null,
    tlvData: tlvData
  };
}
/** Get the minimum lamport balance for a mint to be rent exempt
 *
 * @param connection Connection to use
 * @param commitment Desired level of commitment for querying the state
 *
 * @return Amount of lamports required
 */

export function getMinimumBalanceForRentExemptMint(_x4, _x5) {
  return _getMinimumBalanceForRentExemptMint.apply(this, arguments);
}
/** Get the minimum lamport balance for a rent-exempt mint with extensions
 *
 * @param connection Connection to use
 * @param extensions Extension types included in the mint
 * @param commitment Desired level of commitment for querying the state
 *
 * @return Amount of lamports required
 */

function _getMinimumBalanceForRentExemptMint() {
  _getMinimumBalanceForRentExemptMint = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2(connection, commitment) {
    return _regeneratorRuntime.wrap(function _callee2$(_context2) {
      while (1) {
        switch (_context2.prev = _context2.next) {
          case 0:
            _context2.next = 2;
            return getMinimumBalanceForRentExemptMintWithExtensions(connection, [], commitment);

          case 2:
            return _context2.abrupt("return", _context2.sent);

          case 3:
          case "end":
            return _context2.stop();
        }
      }
    }, _callee2);
  }));
  return _getMinimumBalanceForRentExemptMint.apply(this, arguments);
}

export function getMinimumBalanceForRentExemptMintWithExtensions(_x6, _x7, _x8) {
  return _getMinimumBalanceForRentExemptMintWithExtensions.apply(this, arguments);
}
/**
 * Async version of getAssociatedTokenAddressSync
 * For backwards compatibility
 *
 * @param mint                     Token mint account
 * @param owner                    Owner of the new account
 * @param allowOwnerOffCurve       Allow the owner account to be a PDA (Program Derived Address)
 * @param programId                SPL Token program account
 * @param associatedTokenProgramId SPL Associated Token program account
 *
 * @return Promise containing the address of the associated token account
 */

function _getMinimumBalanceForRentExemptMintWithExtensions() {
  _getMinimumBalanceForRentExemptMintWithExtensions = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee3(connection, extensions, commitment) {
    var mintLen;
    return _regeneratorRuntime.wrap(function _callee3$(_context3) {
      while (1) {
        switch (_context3.prev = _context3.next) {
          case 0:
            mintLen = getMintLen(extensions);
            _context3.next = 3;
            return connection.getMinimumBalanceForRentExemption(mintLen, commitment);

          case 3:
            return _context3.abrupt("return", _context3.sent);

          case 4:
          case "end":
            return _context3.stop();
        }
      }
    }, _callee3);
  }));
  return _getMinimumBalanceForRentExemptMintWithExtensions.apply(this, arguments);
}

export function getAssociatedTokenAddress(_x9, _x10) {
  return _getAssociatedTokenAddress.apply(this, arguments);
}
/**
 * Get the address of the associated token account for a given mint and owner
 *
 * @param mint                     Token mint account
 * @param owner                    Owner of the new account
 * @param allowOwnerOffCurve       Allow the owner account to be a PDA (Program Derived Address)
 * @param programId                SPL Token program account
 * @param associatedTokenProgramId SPL Associated Token program account
 *
 * @return Address of the associated token account
 */

function _getAssociatedTokenAddress() {
  _getAssociatedTokenAddress = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee4(mint, owner) {
    var allowOwnerOffCurve,
        programId,
        associatedTokenProgramId,
        _yield$PublicKey$find,
        _yield$PublicKey$find2,
        address,
        _args4 = arguments;

    return _regeneratorRuntime.wrap(function _callee4$(_context4) {
      while (1) {
        switch (_context4.prev = _context4.next) {
          case 0:
            allowOwnerOffCurve = _args4.length > 2 && _args4[2] !== undefined ? _args4[2] : false;
            programId = _args4.length > 3 && _args4[3] !== undefined ? _args4[3] : TOKEN_PROGRAM_ID;
            associatedTokenProgramId = _args4.length > 4 && _args4[4] !== undefined ? _args4[4] : ASSOCIATED_TOKEN_PROGRAM_ID;

            if (!(!allowOwnerOffCurve && !PublicKey.isOnCurve(owner.toBuffer()))) {
              _context4.next = 5;
              break;
            }

            throw new TokenOwnerOffCurveError();

          case 5:
            _context4.next = 7;
            return PublicKey.findProgramAddress([owner.toBuffer(), programId.toBuffer(), mint.toBuffer()], associatedTokenProgramId);

          case 7:
            _yield$PublicKey$find = _context4.sent;
            _yield$PublicKey$find2 = _slicedToArray(_yield$PublicKey$find, 1);
            address = _yield$PublicKey$find2[0];
            return _context4.abrupt("return", address);

          case 11:
          case "end":
            return _context4.stop();
        }
      }
    }, _callee4);
  }));
  return _getAssociatedTokenAddress.apply(this, arguments);
}

export function getAssociatedTokenAddressSync(mint, owner) {
  var allowOwnerOffCurve = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
  var programId = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : TOKEN_PROGRAM_ID;
  var associatedTokenProgramId = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : ASSOCIATED_TOKEN_PROGRAM_ID;
  if (!allowOwnerOffCurve && !PublicKey.isOnCurve(owner.toBuffer())) throw new TokenOwnerOffCurveError();

  var _PublicKey$findProgra = PublicKey.findProgramAddressSync([owner.toBuffer(), programId.toBuffer(), mint.toBuffer()], associatedTokenProgramId),
      _PublicKey$findProgra2 = _slicedToArray(_PublicKey$findProgra, 1),
      address = _PublicKey$findProgra2[0];

  return address;
}